import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import { ReplaySubject, distinctUntilChanged, filter, of, switchMap, takeUntil, tap } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { AuthService } from 'app/services/auth.service';
import { ModalErrorComponent, ModalErrorData } from 'app/shared/modal-error/modal-error.component';
import { HelpCenterUrls } from 'app/utils/help-center';
import { Loggable } from 'app/utils/logging/loggable';
import { notUndefined } from 'app/utils/stream-util';
import Social from 'entity/Social';

import { ModalSimpleSocialComponent } from '../../modal-simple-social/modal-simple-social.component';
import { VideoEffect, VideoEffectsMode, socialAppSelectors } from '../../state/app';
import { SimpleMediaDeviceInfo, mediaSelectors } from '../../state/media';
import { CAMERA_UNREADABLE_HELP_TEXT, CAMERA_UNREADABLE_TEXT, CAMERA_UNREADABLE_TITLE } from '../../strings';
import { BrowserService } from '../browser.service';
import { DeviceService } from '../device/device.service';
import { doesBrowserSupportVideoEffects } from '../device/utils';
import { MultimediaUserService } from '../multimedia-user.service';
import DevicePermissionStatus, { PermissionsService } from '../permissions.service';
import { VideoEffectService } from '../video-effect.service';

import { ModalSimpleMessageService } from './../../../../shared/modals/modal-simple-message.service';

@Injectable({
  providedIn: 'root',
})
export class DevicePermissionService extends Loggable {
  private destroyed$ = new ReplaySubject<boolean>(1);
  public permission = new DevicePermissionStatus();
  public social?: Social;

  // Devices variables
  public cameras: SimpleMediaDeviceInfo[] = [];
  public mics: SimpleMediaDeviceInfo[] = [];
  public speakers: SimpleMediaDeviceInfo[] = [];

  public selectedCamera: SimpleMediaDeviceInfo | null = null;
  public selectedMic: SimpleMediaDeviceInfo | null = null;
  public selectedSpeaker: SimpleMediaDeviceInfo | null = null;

  public isVideoReady = false;
  public isMicReady = false;
  public isAudioReady = false;

  public isVideoEnabled = true;
  public isMicEnabled = true;
  public areVideoEffectsEnabled = false;

  // Customer API Settings
  public setGuestVideo = false;
  private muteGuestVideo = false;
  private setGuestAudio = false;
  private muteGuestAudio = false;

  public isCameraUnreadable = false;
  private isTestUser = false;

  public ready = true;
  public shouldShow = true;
  public isHost = false;

  // Modals
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private dialogRef: any;

  public constructor(
    private store: Store,
    public dialog: MatDialog,
    private modalSimpleMessageService: ModalSimpleMessageService,
    public deviceService: DeviceService,
    private multimediaUserService: MultimediaUserService,
    private videoEffectService: VideoEffectService,
    private permissionsService: PermissionsService,
    private browserService: BrowserService,
    private authService: AuthService,
  ) {
    super();

    this.store
      .select(coreSelectors.isTestUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isTestUser) => (this.isTestUser = isTestUser));

    this.store
      .select(socialAppSelectors.isGuestCameraStateApplied)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((state) => {
        this.setGuestVideo = state;
      });

    this.store
      .select(socialAppSelectors.muteGuestCamera)
      .pipe(filter(notUndefined), takeUntil(this.destroyed$))
      .subscribe((setting) => {
        this.muteGuestVideo = setting;
      });

    this.store
      .select(socialAppSelectors.isGuestMicStateApplied)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((state) => {
        this.setGuestAudio = state;
      });

    this.store
      .select(socialAppSelectors.muteGuestMic)
      .pipe(filter(notUndefined), takeUntil(this.destroyed$))
      .subscribe((setting) => {
        this.muteGuestAudio = setting;
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.video_background_blur)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (areEffectsEnabled) =>
          (this.areVideoEffectsEnabled =
            areEffectsEnabled &&
            doesBrowserSupportVideoEffects(this.browserService.browserType) &&
            !this.browserService.isAndroidDevice()),
      );

    this.store
      .select(socialAppSelectors.isGuestBlurEffectApplied)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((applyBlur) => {
        if (applyBlur && this.areVideoEffectsEnabled) {
          const effect: VideoEffect = {
            enabled: true,
            mode: VideoEffectsMode.BLUR,
            blurRadius: 3,
          };
          this.videoEffectService.applyEffect(effect);
        }
      });

    this.store
      .select(mediaSelectors.isMicEnabled)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enabled) => (this.isMicEnabled = enabled));

    this.store
      .select(mediaSelectors.isVideoEnabled)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enabled) => (this.isVideoEnabled = enabled));

    this.store
      .select(mediaSelectors.getCameras)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((cameras) => {
        this.cameras = cameras;
      });

    this.store
      .select(mediaSelectors.getSelectedCamera)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((camera) => {
        this.selectedCamera = camera;
        this.isVideoReady = false;
      });

    this.store
      .select(mediaSelectors.getMics)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((mics) => (this.mics = mics));

    this.store
      .select(mediaSelectors.getSelectedMic)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((mic) => {
        this.selectedMic = mic;
        this.isMicReady = true;
      });

    this.store
      .select(mediaSelectors.getSpeakers)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((speakers) => (this.speakers = speakers));

    this.store
      .select(mediaSelectors.getSelectedSpeaker)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((speaker) => {
        this.selectedSpeaker = speaker;
        this.isAudioReady = !!speaker;
      });

    this.store
      .select(mediaSelectors.isCurrentCameraReadable)
      .pipe(
        distinctUntilChanged(),
        tap((readable) => (this.isCameraUnreadable = !readable)),
        switchMap((readable) => {
          if (readable) {
            return of(undefined);
          }
          const url = HelpCenterUrls.SCOOT_ENABLE_CAMERA_AND_MIC;
          const data: ModalErrorData = {
            title: CAMERA_UNREADABLE_TITLE,
            text: CAMERA_UNREADABLE_TEXT,
            helpLink: {
              url: url,
              text: CAMERA_UNREADABLE_HELP_TEXT,
            },
          };

          return this.dialog
            .open<ModalErrorComponent, ModalErrorData>(ModalErrorComponent, {
              data,
            })
            .afterClosed();
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe();
  }

  /**
   *  PERMISSIONS HANDLING
   */
  public async initPermissions(): Promise<void> {
    try {
      this.trace('Initializing Permissions');
      const permission = await this.permissionsService.getPermissionStatus();
      this.info('Got Permissions with Status: ', permission);
      this.permissionsService.didUpdatePermission
        .pipe(
          switchMap((perm) => this.handlePermissions(perm)),
          takeUntil(this.destroyed$),
        )
        .subscribe();
      await this.handlePermissions(permission);
    } catch (error) {
      this.error('Error Initializing Permissions', error);
    }
  }

  public async handlePermissions(permission: DevicePermissionStatus): Promise<void> {
    this.permission = permission;

    // Check Customer API Video Settings
    if (this.setGuestVideo && this.muteGuestVideo) {
      this.isVideoReady = true;
      this.setVideoState();
    }

    // Check Customer API Mic Settings
    if (this.setGuestAudio && this.muteGuestAudio) {
      this.isAudioReady = true;
      this.setAudioState();
    }

    await this.loadAndSetDevices(permission.audioGranted, permission.videoGranted);
  }

  /* handle mic, speaker and camera devices */
  public async loadAndSetDevices(audioGranted: boolean, videoGranted: boolean): Promise<void> {
    if (!audioGranted && !videoGranted) {
      return;
    }

    try {
      await this.deviceService.initializeDevices({ audio: audioGranted, video: videoGranted });
    } catch (error) {
      if (!this.isTestUser) {
        this.error('Error Loading and Setting Devices ', error);
        this.showInitDeviceError();
        return;
      }
    }

    if (this.isTestUser) {
      this.setDevicesReadyFlags(true);
      return;
    }

    if (!(this.selectedMic && this.selectedSpeaker)) {
      this.setDevicesReadyFlags(false);
      this.showInitDeviceError();
    }
  }

  /**
   *  CAMERA HANDLING
   */
  public async setCamera(camera: SimpleMediaDeviceInfo): Promise<void> {
    this.isVideoReady = false;
    if (!camera) {
      this.info('Tried to set invalid camera');
      return;
    }
    await this.deviceService.changeCameraDevice(camera);

    if (!isEqual(this.selectedCamera, camera)) {
      this.showDeviceError(camera.label);
    }
  }

  /**
   *  MICROPHONE HANDLING
   */
  public async setMic(mic: SimpleMediaDeviceInfo): Promise<void> {
    this.isMicReady = false;
    if (!mic) {
      this.warn('Tried to set invalid mic');
      return;
    }

    await this.deviceService.changeMicDevice(mic);

    if (!isEqual(this.selectedMic, mic)) {
      this.showDeviceError(mic.label);
      this.isMicReady = false;
    }
  }

  /**
   *  SPEAKER HANDLING
   */
  public setSpeaker(speaker?: SimpleMediaDeviceInfo): void {
    if (!speaker) {
      this.warn('Tried to set invalid speaker');
      return;
    }
    this.info(`Setting speaker to ${speaker.label} with id ${speaker.deviceId}`);
    this.selectedSpeaker = speaker;
    const service = this.deviceService;
    service.changeSpeakerDevice(speaker);

    if (!isEqual(this.selectedSpeaker, speaker)) {
      this.showDeviceError(speaker.label);
    }
  }

  //** States and Flags */
  public onVideoPlaying = (): void => {
    this.isVideoReady = true;
  };

  public onVideoTimeout = (): void => {
    this.showDeviceError(this.selectedCamera?.label ?? 'unknown camera');
  };

  public setVideoState(): void {
    this.multimediaUserService.toggleVideo();
  }

  public shouldCameraButtonDisabled(): boolean {
    return this.cameras.length === 0 || this.isCameraUnreadable;
  }

  public shouldDisableVideoEffects(): boolean {
    return !this.areVideoEffectsEnabled || !this.isVideoEnabled || !this.isVideoReady;
  }

  public shouldShowAvatarPicture(): boolean {
    return !this.isVideoEnabled || !this.permission.videoGranted || !this.isVideoReady;
  }

  public shouldShowAvatarVideo(): boolean {
    return !(this.isVideoEnabled && this.permission.videoGranted && this.isVideoReady);
  }

  public setAudioState(): void {
    this.multimediaUserService.userToggleAudio();
  }

  public setDevicesReadyFlags(ready: boolean): void {
    this.isMicReady = ready;
    this.isVideoReady = ready;
  }

  public get canUserJoin(): boolean {
    return (
      (this.isVideoReadyOrOff || this.setGuestVideo) &&
      this.isAudioReady &&
      this.isMicReady &&
      this.permission.audioGranted
    );
  }

  private get isVideoDisabled(): boolean {
    if (this.cameras.length === 0 && this.permissionsService.hasCheckedDevices) {
      return true;
    }

    if (this.permissionsService.isVideoPermissionDenied) {
      return true;
    }

    if (this.isCameraUnreadable) {
      return true;
    }

    return false;
  }

  public get isVideoPermissionDenied(): boolean {
    return this.permissionsService.isVideoPermissionDenied;
  }

  public get isAudioPermissionDenied(): boolean {
    return this.permissionsService.isAudioPermissionDenied;
  }

  /**
   * Determine if the users video is ready to use, or is disabled in some way
   * A user may still join if they deny camera permissions, or if they have no camera
   */
  public get isVideoReadyOrOff(): boolean {
    return this.isVideoDisabled || this.isVideoReady;
  }

  /* Modals */
  public showInitDeviceError(): void {
    const data = {
      modalTitle: 'Failed Initializing Devices',
      text: 'Looks like the browser was unable to access your devices. This usually happens when other applications are using your camera or mic. Please quit all other applications using your camera or mic and reload the page.',
      primaryButtonText: 'Reload',
    };
    const dialogRef = this.dialog.open(ModalSimpleSocialComponent, {
      panelClass: 'social-modal',
      data: data,
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((_result) => {
        if (_result === 'primary') {
          void navigator.permissions
            .query({ name: 'microphone' as PermissionName })
            .then((micPermission) => {
              if (micPermission.state === 'granted') {
                window.location.reload();
              }
            })
            .catch((e) => {
              // Handle any errors that might come up when querying permissions
              this.error('Error mic permissions', e);
            });
        }
      });
  }

  public showDeviceError(deviceLabel: string): void {
    const message = {
      title: 'Oops!',
      text: `The device ${deviceLabel} is not available. Please check if another application is using it.`,
    };
    this.dialog.open<ModalErrorComponent, ModalErrorData>(ModalErrorComponent, {
      data: message,
    });
  }

  public showExplanationModal(cameraAccess: boolean, micAccess: boolean): void {
    const modalOpen = this.modalSimpleMessageService.checkIfModalOpen();
    if (!modalOpen) {
      let modalTitle = '';
      let message = '';
      if (!cameraAccess && !micAccess) {
        modalTitle = 'Microphone and camera are blocked';
        message = 'Please update your browser permissions to give Scoot access to your Microphone and Camera.';
      } else if (!cameraAccess) {
        modalTitle = 'Camera is blocked';
        message = 'Please update your browser permissions to give Scoot access to your Camera.';
      } else {
        modalTitle = 'Microphone is blocked';
        message = 'Please update your browser permissions to give Scoot access to your Microphone.';
      }

      const data = {
        title: modalTitle,
        titleIcon: 'SWIRL_ARROW',
        message,
        showActionButton: false,
        secondaryButtonTitle: 'Dismiss',
        tertiaryButtonTitle: 'Get Help',
        tertiaryLink: HelpCenterUrls.SCOOT_ENABLE_CAMERA_AND_MIC,
        showSecondaryButton: true,
        showTertiaryButton: true,
        showClose: true,
        showDone: false,
      };
      this.dialogRef = this.modalSimpleMessageService.throw(
        {
          ...data,
        },
        true,
        false,
        true,
      );
    }
  }
}
